import {
  BodySmall,
  CheckIcon,
  DotMenuHorizontalIcon,
  LanguageIcon,
  Refresh,
  Tooltip,
  theme,
  withAlpha,
} from '@arnold/common';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { DndProvider, useDragDropManager } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ClassicScheme, Drag, RenderEmit } from 'rete-react-plugin';
import { Control, Output, Socket } from 'rete/_types/presets/classic';
import { ReactComponent as ErrorWarningIcon } from '../../../assets/images/ErrorWarning.svg';
import { ReactComponent as LockedIcon } from '../../../assets/images/Locked.svg';
import { TranslationStatus, getNodeTranslationStatus } from '../utils';
import { QuestionType } from '../../../generated/hooks';
import { OptionControl, OptionControlData } from '../controls/OptionControl';
import {
  applyTranslationResultToNode,
  getDefaultSourceTranslation,
  getSourceTranslationContent,
} from '../translationUtils';
import { DataNode, OutputKey, Schemes } from '../dataNode';
import { ValidationError, validateTranslationTexts } from '../validation';
import { COMMENT_TYPE } from '../constants';
import { Draggable } from './Draggable';
import { RefControl } from './RefControl';
import { RefSocket } from './RefSocket';
import { StatementControlData } from '../controls/StatementControl';
import { TFunction } from 'i18next';

type NodeStylesData = {
  width?: number;
  height?: number;
  readOnly?: boolean;
  errors: ValidationError[];
  lockEdit: boolean;
  showLockIcon?: boolean;
  aiDiffStatus?: 'added' | 'modified';
};

const nodeWidth = 250;

const NodeStyles = styled.div<NodeStylesData & { selected: boolean; styles?: (props: any) => any; isComment: boolean }>`
  background: ${theme.background.primary};
  border-radius: 10px;
  cursor: pointer;
  box-sizing: border-box;
  width: ${(props) => (Number.isFinite(props.width) ? `${props.width}px` : `${nodeWidth}px`)};
  height: auto !important;
  padding-bottom: 6px;
  position: relative;
  user-select: none;
  line-height: initial;
  font-size: ${theme.typography.body.small.regular.fontSize};
  box-shadow: ${theme.shadows.elevationContentBlock};
  transition: all 0.2s ease-in-out;
  border: ${(props) => (props.isComment ? `${theme.spacing.a} solid ${theme.colors.emotionWarning.default}` : 'none')};
  &:hover {
    box-shadow: 0px 10px 14px 0px rgba(0, 0, 0, 0.05);
  }
  .title {
    color: ${(props) => (props.isComment ? theme.colors.text.primary : theme.colors.textInverted.primary)};
    background-color: ${(props) =>
      props.isComment ? theme.colors.emotionWarning.default : theme.colors.backgroundDark.default};
    border-top-left-radius: 9px;
    transition: background-color 0.2s ease-in-out;
    border-top-right-radius: 9px;
    font-family: sans-serif;
    font-size: ${theme.typography.body.small.regular.fontSize};
    padding: ${theme.spacing.d} ${theme.spacing.f};
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    .iconContainer {
      align-self: stretch;
      display: flex;
      align-items: center;
      svg {
        height: 12px;
        path,
        circle {
          fill: white;
        }
      }
    }
    .threeDots {
      svg {
        transform: rotate(90deg);
        height: ${theme.spacing.f};
        scale: 1.5;
        & circle {
          fill: ${(props) => (props.isComment ? theme.colors.text.primary : theme.colors.textInverted.primary)};
        }
      }
    }
  }
  .titleText {
    margin-left: ${(props) => (props.isComment ? '0' : '-25px')};
    font-weight: normal;
    display: flex;
    & svg {
      margin-bottom: ${theme.spacing.b};
      margin-right: ${theme.spacing.c};
    }
  }
  &:focus-within {
    box-shadow: 0px 10px 14px 0px rgba(0, 0, 0, 0.05) !important;
    .title {
      background-color: ${(props) =>
        props.isComment ? theme.colors.emotionWarning.default : theme.colors.actionPrimary.default};
    }
  }
  ${(props) =>
    props.selected &&
    css`
      box-shadow: 0px 10px 14px 0px rgba(0, 0, 0, 0.05) !important;
      .title {
        background-color: ${props.isComment ? theme.colors.emotionWarning.active : theme.colors.actionPrimary.default};
      }
    `}
  ${(props) =>
    props.errors.length > 0 &&
    css`
      .title {
        background-color: ${theme.colors.emotionDanger.default} !important;
      }
    `}
  ${(props) =>
    props.aiDiffStatus === 'added' &&
    css`
      box-shadow:
        0 0 0 2px ${theme.colors.emotionSuccess.default},
        0 0 18px 4px ${theme.colors.emotionSuccess.background} !important;
      .title {
        background-color: ${theme.colors.emotionSuccess.default} !important;
        color: ${theme.colors.textInverted.primary} !important;
      }
      .threeDots svg circle {
        fill: ${theme.colors.textInverted.primary} !important;
      }
    `}
  ${(props) =>
    props.aiDiffStatus === 'modified' &&
    css`
      box-shadow:
        0 0 0 2px ${theme.colors.ai.default},
        0 0 18px 4px rgba(155, 55, 255, 0.35) !important;
      .title {
        background-color: ${theme.colors.ai.default} !important;
        color: ${theme.colors.textInverted.primary} !important;
      }
      .threeDots svg circle {
        fill: ${theme.colors.textInverted.primary} !important;
      }
    `}
  ${(props) => css`
    &:focus-within {
      box-shadow: 0px 10px 14px 0px rgba(0, 0, 0, 0.05) !important;
      .title {
        background-color: ${props.isComment
          ? theme.colors.emotionWarning.active
          : theme.colors.actionPrimary.default} !important;
      }
    }
  `}
  ${(props) =>
    props.selected &&
    !props.isComment &&
    css`
      box-shadow: 0px 10px 14px 0px rgba(0, 0, 0, 0.05) !important;
      .title {
        background-color: ${theme.colors.actionPrimary.default} !important;
      }
    `}
  ${(props) =>
    props.errors.map(
      (error) => css`
        [data-error-part='${error.part}'] {
          .textAreaControl,
          .optionControl,
          input[name='min'],
          input[name='max'] {
            outline: 1px solid ${theme.colors.emotionDanger.default} !important;
            border: none !important;
            box-shadow: none !important;
            &:focus,
            &:focus-within {
              box-shadow: 0 0 0rem 0.2rem #cc454d3f !important;
            }
          }
          .reportOrderInput {
            background-color: white !important;
            color: ${theme.colors.backgroundDark.default};
          }
          div[title='socket'] {
            background-color: ${theme.colors.emotionDanger.default} !important;
          }
        }
      `,
    )}
    ${(props) =>
    props.lockEdit &&
    css`
      pointer-events: none;
      .title {
        background-color: ${theme.colors.text.disabled} !important;
      }
      .output-socket,
      .input-socket,
      .errorTooltip,
      .threeDots {
        pointer-events: auto;
        margin-left: ${theme.spacing.c};
      }
    `}
  .reportOrderInput {
    padding: 2px !important;
    margin: 0px !important;
    width: ${theme.spacing.h};
    outline: none;
    color: white;
    background-color: transparent !important;
  }
  .reportOrderInput:hover,
  .reportOrderInput:focus {
    color: ${theme.colors.backgroundDark.default};
    background-color: white !important;
  }
  .textAreaSpan,
  input[type='text'] {
    transition: all 0.2s ease-in-out;
    padding: 8px;
    border: none;
    border-radius: 5px !important;
    background-color: ${theme.colors.backgroundBasic.default};
  }
  .textAreaSpan {
    display: block;
    resize: none;
    overflow: hidden;
    margin: ${theme.spacing.d} ${theme.spacing.f};
    padding: ${theme.spacing.d} ${theme.spacing.f};
    cursor: text;
    border: 1px solid ${theme.colors.backgroundBasic.default};
    outline: none;

    &:focus {
      border-color: ${theme.colors.borderMain.active};
      box-shadow: 0 0 0 0.2rem ${withAlpha(theme.colors.actionPrimary.default, 0.25)};
    }
  }
  input[type='text'] {
    margin-top: 5px;
    margin-bottom: 5px;
  }
  .checkboxAndLabel {
    margin: ${theme.spacing.f} ${theme.spacing.f} 0 ${theme.spacing.f};
    display: flex;
  }
  .horizontal {
    display: flex;
    justify-content: space-between;
    & > span {
      width: 100%;
    }
  }
  .inputs {
    margin-top: ${theme.spacing.d};
    margin-bottom: ${theme.spacing.d};
  }
  .controlsAndOutputs {
    flex-grow: 1;
  }
  .statement,
  .controlWithoutOutput,
  .controlAndOutput {
    display: flex;
    align-items: center;
    flex-grow: 1;
    justify-content: space-between;
    margin-bottom: ${theme.spacing.d};
  }
  .statement .control,
  .controlWithoutOutput .control,
  .controlAndOutput .control {
    flex-grow: 1;
  }
  .statement,
  .controlWithoutOutput {
    margin-right: ${theme.spacing.f};
  }
  .output {
    text-align: right;
  }
  .input {
    text-align: left;
  }
  div[title='socket'] {
    width: 16px;
    height: 16px;
    background-color: ${theme.colors.actionPrimary.default};
    border: none;
    transition: all 0.3s;
  }
  .output-socket {
    text-align: right;
    margin-right: -10px;
    margin-left: 7px;
    display: inline-block;
  }
  .input-socket {
    text-align: left;
    margin-left: -10px;
    margin-right: 7px;
    display: inline-block;
  }
  .output-socket > div,
  .input-socket > div {
    padding: 0px !important;
  }
  .input-control {
    z-index: 1;
    vertical-align: middle;
    display: inline-block;
  }
  .control {
    display: block;
  }

  hr {
    margin: ${theme.spacing.f};
  }
  .label {
    margin-left: ${theme.spacing.f};
    margin-bottom: ${theme.spacing.d};
    color: ${theme.colors.text.secondary};
  }
  .fakeEscapeOption {
    margin-left: ${theme.spacing.f};
    margin-right: ${theme.spacing.f};
    color: ${theme.colors.text.secondary};
    pointer-events: none;
  }

  ${(props) => props.styles && props.styles(props)}
`;

const TranslationStatusBanner = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.d};
  margin: ${theme.spacing.d} ${theme.spacing.f} 0;
  padding: ${theme.spacing.e};
  background-color: ${theme.colors.emotionWarning.background};
  border: 1px solid ${theme.colors.emotionWarning.backgroundHover};
  border-radius: ${theme.borderRadius.m};
`;

const TranslationStatusRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.d};
`;

const TranslationStatusIcon = styled.div`
  flex-shrink: 0;
  margin-top: 1px;
  svg {
    height: 14px;
    width: 14px;
    path,
    circle {
      fill: ${theme.colors.emotionWarning.default};
    }
  }
`;

const TranslationStatusText = styled.span`
  font-size: ${theme.typography.body.small.regular.fontSize};
  font-weight: 500;
  color: ${theme.colors.emotionWarning.hover};
  line-height: 1.35;
`;

const SourceLanguagePill = styled.span`
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: ${theme.spacing.c};
  padding: ${theme.spacing.b} ${theme.spacing.d};
  background-color: ${theme.colors.emotionWarning.backgroundHover};
  color: ${theme.colors.emotionWarning.hover};
  border-radius: ${theme.borderRadius.xxl};
  font-size: 10px;
  font-weight: 500;
  white-space: nowrap;
  svg {
    height: 12px;
    width: 12px;
    path {
      fill: currentColor;
    }
  }
`;

const SourceTextBox = styled.div`
  padding: ${theme.spacing.d} ${theme.spacing.e};
  background-color: ${theme.background.primary};
  border: 1px solid ${theme.colors.emotionWarning.backgroundHover};
  border-radius: ${theme.borderRadius.s};
  font-size: ${theme.typography.body.small.regular.fontSize};
  color: ${theme.colors.text.secondary};
  line-height: 1.45;
  font-style: italic;
`;

const SourceChoiceRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ChoiceTextBox = styled.div<{ dimmed?: boolean }>`
  padding: ${theme.spacing.c} ${theme.spacing.e};
  background-color: ${theme.background.primary};
  border: 1px solid ${theme.colors.emotionWarning.backgroundHover};
  border-radius: ${theme.borderRadius.s};
  font-size: ${theme.typography.body.small.regular.fontSize};
  color: ${(props) => (props.dimmed ? theme.colors.text.disabled : theme.colors.text.secondary)};
  font-style: italic;
  line-height: 1.45;
`;

const BannerActions = styled.div<{ hasOptions: boolean }>`
  display: flex;
  gap: ${theme.spacing.d};
  padding: ${theme.spacing.d} ${theme.spacing.f} 0;
  ${(props) =>
    props.hasOptions &&
    css`
      padding-bottom: ${theme.spacing.f};
    `}
`;

const BannerActionButton = styled.button<{ variant?: 'primary' | 'default' }>`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.c};
  border: 1px solid
    ${(props) => (props.variant === 'primary' ? theme.colors.iconAction.primary : theme.colors.borderMain.default)};
  border-radius: ${theme.borderRadius.m};
  background: ${(props) => (props.variant === 'primary' ? theme.colors.iconAction.primary : 'transparent')};
  color: ${(props) => (props.variant === 'primary' ? 'white' : theme.colors.text.secondary)};
  font-size: 11px;
  font-weight: 500;
  padding: ${theme.spacing.c} ${theme.spacing.e};
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s;

  svg {
    height: 13px;
    width: 13px;
    path {
      fill: currentColor;
    }
  }

  &:hover {
    background: ${(props) =>
      props.variant === 'primary' ? theme.colors.actionPrimary.hover : theme.colors.backgroundBasic.hover};
    border-color: ${(props) =>
      props.variant === 'primary' ? theme.colors.actionPrimary.hover : theme.colors.iconAction.primary};
    color: ${(props) => (props.variant === 'primary' ? 'white' : theme.colors.text.primary)};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

function sortByIndex<T extends [string, undefined | { index?: number }][]>(entries: T) {
  entries.sort((a, b) => {
    const ai = a[1]?.index || 0;
    const bi = b[1]?.index || 0;

    return ai - bi;
  });
}

function isLockedPairSocketDisabled(node: DataNode, side: 'input' | 'output', socketKey: string): boolean {
  if (!node.lockEdit) return false;
  if (side === 'input') {
    return node.topicEditorInputSocketLockedPair;
  }
  const targetQuestionIndex = node.data.outputs?.[socketKey as OutputKey];
  if (targetQuestionIndex == null) return false;
  return node.topicEditorLockEditQuestionIndices.includes(targetQuestionIndex);
}

type OptionsProps<S extends ClassicScheme> = {
  controlsWithOutputs: {
    control: [string, Control | undefined];
    output: [string, Output<Socket> | undefined];
  }[];
  readOnly: boolean;
  id: string;
  emit: RenderEmit<S>;
  isSomethingElseDragged: boolean;
  setIsSomethingElseDragged: (isSomethingElseDragged: boolean) => void;
  nodeData: DataNode;
};

function Options<Scheme extends ClassicScheme>({
  controlsWithOutputs,
  readOnly,
  id,
  emit,
  isSomethingElseDragged,
  setIsSomethingElseDragged,
  nodeData,
}: OptionsProps<Scheme>) {
  const dragDropManager = useDragDropManager();
  const monitor = dragDropManager.getMonitor();

  useEffect(
    () => {
      const unsubscribe = monitor.subscribeToStateChange(() => {
        setIsSomethingElseDragged(monitor.isDragging());
      });

      return () => {
        unsubscribe();
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [monitor],
  );

  return (
    <div className={classNames('controlsAndOutputs', isSomethingElseDragged && 'isSomethingDragged')}>
      {controlsWithOutputs.map(({ control: [key, control], output: [_key, output] }, index) => {
        if (control == null) throw new Error("Control with output can't have undefined control");
        const typedControl = control as OptionControlData;
        return (
          <div
            className={classNames('justify-content-end', output ? 'controlAndOutput' : 'controlWithoutOutput')}
            key={key}
          >
            <Draggable
              index={index}
              id={typedControl.id}
              move={typedControl.onMove}
              readOnly={readOnly}
              isSomethingDragged={isSomethingElseDragged}
            >
              <RefControl
                name="control"
                emit={emit}
                payload={control}
                data-cy={`control-${key}`}
                data-error-part={`choice-${key}`}
              />
            </Draggable>
            {output && (
              <div className="output" data-cy={`output-${key}`} data-error-part={`output-${key}`}>
                {output.label && (
                  <div className="output-title" data-cy="output-title">
                    {output.label}
                  </div>
                )}
                <RefSocket
                  name="output-socket"
                  side="output"
                  socketKey={key}
                  nodeId={id}
                  emit={emit}
                  payload={output.socket}
                  data-cy="output-socket"
                  disabled={isLockedPairSocketDisabled(nodeData, 'output', key)}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

type StatementsProps<S extends ClassicScheme> = {
  controls: StatementControlData[];
  readOnly: boolean;
  emit: RenderEmit<S>;
  isSomethingElseDragged: boolean;
  setIsSomethingElseDragged: (isSomethingElseDragged: boolean) => void;
};

function Statements<Scheme extends ClassicScheme>({
  controls,
  readOnly,
  emit,
  isSomethingElseDragged,
  setIsSomethingElseDragged,
}: StatementsProps<Scheme>) {
  const dragDropManager = useDragDropManager();
  const monitor = dragDropManager.getMonitor();

  useEffect(
    () => {
      const unsubscribe = monitor.subscribeToStateChange(() => {
        setIsSomethingElseDragged(monitor.isDragging());
      });

      return () => {
        unsubscribe();
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [monitor],
  );

  return (
    <div className={classNames(isSomethingElseDragged && 'isSomethingDragged')}>
      {controls.map((control, index) => {
        return (
          <div className="statement justify-content-end" key={control.key}>
            <Draggable
              index={index}
              id={control.key}
              move={control.onMove}
              readOnly={readOnly}
              isSomethingDragged={isSomethingElseDragged}
            >
              <RefControl
                name="control"
                emit={emit}
                payload={control}
                data-cy={`control-${control.key}`}
                data-error-part={`statement-${index}`}
              />
            </Draggable>
          </div>
        );
      })}
    </div>
  );
}

type Props<S extends ClassicScheme> = {
  data: S['Node'];
  styles?: () => any;
  emit: RenderEmit<S>;
};

// eslint-disable-next-line max-statements
export function CustomNode(props: Props<Schemes>) {
  const [isSomethingElseDragged, setIsSomethingElseDragged] = useState(false);
  const [translationStatus, setTranslationStatus] = useState<TranslationStatus>(() =>
    getNodeTranslationStatus(props.data.data.originalQuestionDefinition, props.data.languageCode),
  );
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    const override = props.data.data.translationStatusOverride;
    if (override != null) {
      setTranslationStatus(override as TranslationStatus);
    }
  }, [props.data.data.translationStatusOverride]);

  const handleMarkAsOk = async () => {
    try {
      const questionDefinitionId = props.data.data.originalQuestionDefinition?.id;
      if (!questionDefinitionId || !props.data.data.onMarkTranslationAsReviewed) return;
      await props.data.data.onMarkTranslationAsReviewed(questionDefinitionId, props.data.languageCode);
      setTranslationStatus('ok');
      props.data.data.onTranslationStatusChange?.(props.data.id, 'ok');
    } catch (error) {
      console.error('Error marking as OK:', error);
    }
  };

  const handleAiTranslate = async () => {
    if (!props.data.data.onAiTranslateNode) return;
    const sourceTrans = getDefaultSourceTranslation(props.data.data);
    if (!sourceTrans?.language?.code) return;
    setIsTranslating(true);
    try {
      const { choices: sourceChoices, statements: sourceStatements } = getSourceTranslationContent(sourceTrans);
      const result = await props.data.data.onAiTranslateNode(
        sourceTrans.text,
        sourceTrans.language.code,
        props.data.languageCode,
        sourceChoices,
        sourceStatements,
      );
      applyTranslationResultToNode(props.data as DataNode, result, sourceTrans);
      setTranslationStatus('ok');
      props.data.data.onTranslationStatusChange?.(props.data.id, 'ok');
    } catch (error) {
      console.error('Error translating:', error);
    } finally {
      setIsTranslating(false);
    }
  };

  const inputs = Object.entries(props.data.inputs);
  const outputs = Object.entries(props.data.outputs);
  const controls = Object.entries(props.data.controls).filter(
    (entries) =>
      !['reportOrder', 'addOption', 'questionText', 'addStatement', 'randomizeOrder'].includes(entries[0]) &&
      !entries[0].startsWith('statement-'),
  );
  const errors = props.data.errors;
  const reportOrderControl = props.data.controls.reportOrder!;
  const addOptionControl = props.data.controls.addOption;
  const randomizeOrderControl = props.data.controls.randomizeOrder;
  const questionTextControl = props.data.controls.questionText!;
  const selected = props.data.selected || false;
  const { id, label, width, height, data } = props.data;
  const optionsReadOnly = !!props.data.readOnly || !!data.isRankingScale;
  const aiDiffStatus = data.aiDiffStatus;
  const isSourceLanguage =
    props.data.data.originalQuestionDefinition?.translations.find((t) => t.isDefault)?.language?.code ===
    props.data.languageCode;
  const hasEmptyTranslationText = !isSourceLanguage && validateTranslationTexts([props.data as DataNode]).length > 0;
  const showTranslationIssue =
    data.type !== COMMENT_TYPE &&
    !!props.data.data.onAiTranslateNode &&
    (translationStatus !== 'ok' || hasEmptyTranslationText);

  sortByIndex(inputs);
  sortByIndex(outputs);
  sortByIndex(controls);

  const controlsWithOutputs: { control: (typeof controls)[number]; output: (typeof outputs)[number] }[] = [];
  [...controls].forEach((controlEntry) => {
    if (data.type === QuestionType.Scale) return; // scale should not have outputs at all
    const outputEntry = outputs.find((outputEntry) => outputEntry[0] === controlEntry[0]);
    if (outputEntry) {
      controlsWithOutputs.push({ control: controlEntry, output: outputEntry });
      controls.splice(controls.indexOf(controlEntry), 1); // remove it from controls array
      outputs.splice(outputs.indexOf(outputEntry), 1); // remove it from outputs array
    }
  });

  const errorTooltipContent = (
    <div>
      {errors.map((error) => (
        <div key={error.part + error.error}>{props.data.t(error.error + 'Short')}</div>
      ))}
    </div>
  );

  return (
    <NodeStyles
      selected={selected}
      width={width}
      height={height}
      errors={errors}
      lockEdit={props.data.lockEdit}
      styles={props.styles}
      aiDiffStatus={aiDiffStatus}
      data-cy={`node-${reportOrderControl.initialValue}`}
      data-icom={`node-${data.type}`}
      isComment={data.type === COMMENT_TYPE}
    >
      <div className="title" data-cy="title">
        {data.type !== COMMENT_TYPE && (
          <RefControl
            name="control"
            emit={props.emit}
            payload={reportOrderControl}
            data-cy={`control-reportOrder`}
            data-error-part={'reportOrder'}
          />
        )}
        <span className="titleText">
          {errors.length > 0 && (
            <div className="iconContainer">
              <Tooltip html={errorTooltipContent} className="errorTooltip">
                <ErrorWarningIcon />
              </Tooltip>
            </div>
          )}
          {label}
        </span>
        <div className="iconContainer">
          {(props.data.lockEdit || props.data.showLockIcon) && (
            <div>
              <LockedIcon />
            </div>
          )}
          {!props.data.lockEdit &&
            props.data.data.type !== QuestionType.Ending &&
            props.data.data.type !== COMMENT_TYPE && (
              <Drag.NoDrag>
                <div
                  className="threeDots"
                  onClick={(e) => {
                    let target = e.target as HTMLElement;
                    if (target.tagName === 'circle') {
                      target = target.parentElement!;
                    }
                    const iconRight = target.getBoundingClientRect().right || 0;
                    const iconBottom = target.getBoundingClientRect().bottom || 0;
                    const contextMenuEvent = new PointerEvent('contextmenu', {
                      bubbles: true,
                      clientX: iconRight + 62,
                      clientY: iconBottom + 12,
                    });
                    e.stopPropagation();
                    e.preventDefault();
                    e.target.dispatchEvent(contextMenuEvent);
                  }}
                >
                  <DotMenuHorizontalIcon />
                </div>
              </Drag.NoDrag>
            )}
        </div>
      </div>
      {showTranslationIssue && (
        <Drag.NoDrag>
          <TranslationStatusBanner>
            <TranslationStatusRow>
              <TranslationStatusIcon>
                <ErrorWarningIcon />
              </TranslationStatusIcon>
              <TranslationStatusText>
                {props.data.t(
                  translationStatus === 'missing' || hasEmptyTranslationText
                    ? 'translationStatusMissing'
                    : translationStatus === 'source_changed'
                      ? 'translationStatusSourceChanged'
                      : 'translationStatusSameAsSource',
                )}
              </TranslationStatusText>
            </TranslationStatusRow>
            {(() => {
              const sourceTrans = props.data.data.originalQuestionDefinition?.translations.find((t) => t.isDefault);
              const sourceText = sourceTrans?.text;
              const sourceChoices = (sourceTrans?.choices ?? []).filter((c): c is string => !!c);
              const sourceStatements = (sourceTrans?.statements ?? []).filter((s): s is string => !!s);
              return (
                <>
                  <SourceLanguagePill>
                    <LanguageIcon />
                    {`${props.data.t('translationSourceLanguage')}: ${props.data.t(
                      `languageSelector:${sourceTrans?.language?.code}`,
                    )}`}
                  </SourceLanguagePill>
                  {sourceText ? <SourceTextBox>{sourceText}</SourceTextBox> : null}
                  {sourceChoices.map((choice, i) => {
                    return (
                      <SourceChoiceRow key={i}>
                        <ChoiceTextBox dimmed>{choice}</ChoiceTextBox>
                      </SourceChoiceRow>
                    );
                  })}
                  {sourceStatements.map((statement, i) => {
                    return (
                      <SourceChoiceRow key={`statement-${i}`}>
                        <ChoiceTextBox dimmed>{statement}</ChoiceTextBox>
                      </SourceChoiceRow>
                    );
                  })}
                </>
              );
            })()}
          </TranslationStatusBanner>
        </Drag.NoDrag>
      )}
      <div className="inputs">
        {/* Inputs */}
        {inputs.map(
          ([key, input]) =>
            input && (
              <div className="input" key={key} data-cy={`input-${key}`} data-error-part={'input'}>
                <RefSocket
                  name="input-socket"
                  side="input"
                  socketKey={key}
                  nodeId={id}
                  emit={props.emit}
                  payload={input.socket}
                  data-cy="input-socket"
                  data-icom={'input-ariel'}
                  disabled={isLockedPairSocketDisabled(props.data, 'input', key)}
                />
                {input && (!input.control || !input.showControl) && input?.label && (
                  <div className="input-title" data-cy="input-title">
                    {input?.label}
                  </div>
                )}
                {input?.control && input?.showControl && (
                  <RefControl name="input-control" emit={props.emit} payload={input.control} data-cy="input-control" />
                )}
              </div>
            ),
        )}
      </div>
      {data.type !== COMMENT_TYPE && (
        <RefControl
          name="control"
          emit={props.emit}
          payload={questionTextControl}
          data-cy="control-questionText"
          data-error-part={`questionText`}
        />
      )}
      {(Object.keys(controlsWithOutputs).length > 0 ||
        controls.filter(([key]) => key.startsWith('opt-')).length > 0 ||
        addOptionControl) && <hr />}
      {/* Controls without outputs */}
      {controls
        .filter(([key]) => !key.startsWith('opt-'))
        .map(([key, control]) => {
          return control ? (
            <RefControl
              key={key}
              name="control"
              emit={props.emit}
              payload={control}
              data-cy={`control-${key}`}
              data-error-part={`choice-${key}`}
            />
          ) : null;
        })}
      {props.data.choicesLabel && <div className="label">{props.data.choicesLabel}:</div>}
      <Drag.NoDrag>
        <DndProvider backend={HTML5Backend} context={window}>
          <Options
            nodeData={props.data}
            controlsWithOutputs={controls
              .filter(([key]) => key.startsWith('opt-'))
              .map((ctrl) => ({ control: ctrl, output: [ctrl[0], undefined] }))}
            readOnly={optionsReadOnly}
            id={id}
            emit={props.emit}
            isSomethingElseDragged={isSomethingElseDragged}
            setIsSomethingElseDragged={setIsSomethingElseDragged}
          />
        </DndProvider>
      </Drag.NoDrag>
      {data.type === QuestionType.Scale && data.isRankingScale && (
        <Drag.NoDrag>
          <BodySmall
            margin={`${theme.spacing.f} ${theme.spacing.f} 0 ${theme.spacing.f}`}
            color={theme.colors.text.secondary}
          >
            {props.data.t('rankingScaleDescription')}
          </BodySmall>
        </Drag.NoDrag>
      )}
      {/* fake non editable escape option for scale question */}
      {data.type === QuestionType.Scale && data.showEscapeOption && (
        <Drag.NoDrag>
          <Tooltip title={props.data.t('scaleEscapeOptionTooltip')}>
            <div className="fakeEscapeOption">
              <OptionControl data={getFakeEscapeOptionControlData(props.data.t, props.data.languageCode)} />
            </div>
          </Tooltip>
        </Drag.NoDrag>
      )}
      <Drag.NoDrag>
        <DndProvider backend={HTML5Backend} context={window}>
          <Options
            controlsWithOutputs={controlsWithOutputs}
            readOnly={optionsReadOnly}
            id={id}
            emit={props.emit}
            isSomethingElseDragged={isSomethingElseDragged}
            setIsSomethingElseDragged={setIsSomethingElseDragged}
            nodeData={props.data}
          />
        </DndProvider>
      </Drag.NoDrag>
      {addOptionControl ? (
        <RefControl name="control" emit={props.emit} payload={addOptionControl} data-cy="control-addOption" />
      ) : null}
      {/* Scale statements block */}
      {(Object.keys(props.data.controls).filter((key) => key.startsWith('statement-')).length > 0 ||
        props.data.controls.addStatement) && (
        <>
          <hr />
          {props.data.statementsLabel && <div className="label">{props.data.statementsLabel}:</div>}
          <Drag.NoDrag>
            <DndProvider backend={HTML5Backend} context={window}>
              <Statements
                controls={Object.entries(props.data.controls)
                  .filter(([key]) => key.startsWith('statement-'))
                  .map(([key, ctrl]) => ctrl as StatementControlData)}
                readOnly={!!props.data.readOnly}
                emit={props.emit}
                isSomethingElseDragged={isSomethingElseDragged}
                setIsSomethingElseDragged={setIsSomethingElseDragged}
              />
            </DndProvider>
          </Drag.NoDrag>
          {data.type === QuestionType.Scale && (
            <Drag.NoDrag>
              <BodySmall
                margin={`${theme.spacing.f} ${theme.spacing.f} 0 ${theme.spacing.f}`}
                color={theme.colors.text.secondary}
              >
                {props.data.t('scaleStatementsDescription')}
              </BodySmall>
            </Drag.NoDrag>
          )}
          {props.data.controls.addStatement && (
            <RefControl
              name="control"
              emit={props.emit}
              payload={props.data.controls.addStatement}
              data-cy="control-addStatement"
            />
          )}
          {randomizeOrderControl && (
            <div style={{ marginTop: '-16px' }}>
              <RefControl
                name="control"
                emit={props.emit}
                payload={randomizeOrderControl}
                data-cy="control-randomizeOrder"
              />
            </div>
          )}
        </>
      )}
      {showTranslationIssue && (
        <Drag.NoDrag>
          <BannerActions
            hasOptions={[
              QuestionType.Select,
              QuestionType.Scale,
              QuestionType.PersonMultiselect,
              QuestionType.Multiselect,
              QuestionType.Ending,
            ].includes(props.data.data.type as QuestionType)}
          >
            <BannerActionButton variant="primary" onClick={handleAiTranslate} disabled={isTranslating}>
              {isTranslating ? (
                '...'
              ) : (
                <>
                  <Refresh />
                  {props.data.t('translationAiTranslate')}
                </>
              )}
            </BannerActionButton>
            {translationStatus !== 'missing' && !hasEmptyTranslationText && (
              <BannerActionButton onClick={handleMarkAsOk}>
                <CheckIcon />
                {props.data.t('translationMarkAsOk')}
              </BannerActionButton>
            )}
          </BannerActions>
        </Drag.NoDrag>
      )}
      {/* Outputs without controls */}
      {outputs.map(
        ([key, output]) =>
          output && (
            <div className="output" key={key} data-cy={`output-${key}`} data-error-part={`output-${key}`}>
              {output?.label && (
                <div className="output-title" data-cy="output-title">
                  {output?.label}
                </div>
              )}
              <RefSocket
                name="output-socket"
                side="output"
                socketKey={key}
                nodeId={id}
                emit={props.emit}
                payload={output.socket}
                data-cy="output-socket"
                data-icom="output-socket"
                disabled={isLockedPairSocketDisabled(props.data, 'output', key)}
              />
            </div>
          ),
      )}
    </NodeStyles>
  );
}

const getFakeEscapeOptionControlData = (t: TFunction, language: string): OptionControlData => {
  return {
    id: 'choice-escape',
    initialValue: t(`scaleEscapeOption_${language}`, { lng: 'en' }),
    index: 0,
    key: 'choice-escape',
    severity: 0,
    onChange: () => 0,
    onDelete: () => 0,
    onMove: () => 0,
    onSeverityChange: () => 0,
    readOnly: true,
    showDeleteButton: false,
    canEditSeverity: false,
  };
};
